/**
 * Unified schedule entry card that uses pre-resolved display properties from backend.
 * Modern design matching the native app style.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Theme } from '../../Theme/Theme';
import { WeekDayEntry, getEntryLink } from '../../Model/Coach/Schedule/WeekDayEntry';
import { WeekDayEntryWorkout } from '../../Model/Coach/Schedule/WeekDayEntryWorkout';
import { WeekDayEntryActivityLog } from '../../Model/Coach/Schedule/WeekDayEntryActivityLog';
import { LinkUtils } from '../../Utils/LinkUtils';
import { resolveEntryWebPath } from './entryWebPath';
import { useRootStore } from '../../Store/useRootStore';
import { downloadScheduleEntryAsIcal } from '../../Utils/ical/utils';
import { getMediaMediumOrSmallestUriObject } from '../../Model/Media/Media';

const Container = styled.div<{ disabled?: boolean; clickable?: boolean; done?: boolean }>`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : props.clickable ? 'pointer' : 'default')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  background: ${(props) => (props.done ? `${Theme.Colors.primary}08` : 'white')};
  border-radius: 16px;
  display: flex;
  align-items: stretch;
  min-height: 90px;
  overflow: hidden;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: ${(props) => (props.disabled ? 'none' : 'translateY(-4px)')};
    box-shadow: ${(props) => (props.disabled ? '0 4px 16px rgba(0,0,0,0.1)' : '0 12px 32px rgba(0, 0, 0, 0.15)')};

    .schedule-thumbnail {
      transform: ${(props) => (props.done ? 'none' : 'scale(1.05)')};
    }

    .arrow-indicator {
      transform: translateX(4px);
      opacity: 1;
    }
  }
`;

const ThumbnailContainer = styled.div`
  width: 90px;
  min-width: 90px;
  position: relative;
  overflow: hidden;
`;

const Thumbnail = styled.div<{ imageSource: string }>`
  position: absolute;
  inset: 0;
  background-image: ${(props) => props.imageSource};
  background-size: cover;
  background-position: center;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const PlaceholderThumbnail = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, ${Theme.Colors.primary}25 0%, ${Theme.Colors.primary}10 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.Colors.primary};
  font-size: 28px;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlayIcon = styled.i`
  color: white;
  font-size: 20px;
`;

const Content = styled.div`
  flex: 1;
  padding: 14px 16px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const TypeTag = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #00dda2;
  margin-bottom: 4px;
`;

const TitleText = styled.div`
  font-weight: 600;
  font-size: 15px;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 4px;
`;

const SubtitleText = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${Theme.Colors.warmGrey};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  padding-right: 16px;
  gap: 12px;
`;

const StatusIndicator = styled.div<{ done?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.done ? Theme.Colors.primary : 'white')};
  border: ${(props) => (props.done ? 'none' : `2px solid ${Theme.Colors.lighterGrey}`)};
  transition: all 0.2s ease;

  & > i {
    color: ${Theme.Colors.white};
    font-size: 14px;
  }
`;

const ArrowIndicator = styled.div`
  color: ${Theme.Colors.primary};
  font-size: 18px;
  opacity: 0.5;
  transition: all 0.3s ease;
`;

const CalendarButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${Theme.Colors.primary};
  font-size: 16px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${Theme.Colors.primary}10;
    border-color: ${Theme.Colors.primary};
  }
`;

export type ScheduleEntryCardProps = {
  entry: WeekDayEntry;
  date?: string;
};

export const ScheduleEntryCard: React.FC<ScheduleEntryCardProps> = observer(({ entry, date }) => {
  const navigate = useNavigate();
  const { analytics } = useRootStore();

  const { title, subtitle, media, showAsDone, disabled, typeName, durationText, timeText, contextInfo, isVideo } =
    entry;

  const imageUri = media ? getMediaMediumOrSmallestUriObject(media).uri : undefined;
  const hasImage = !!imageUri;

  // Clickable when we can route the entry: workout / activityLog have dedicated
  // web routes, and any other type is clickable when the backend supplied a link
  // we can resolve to a web path (e.g. scheduled explore-entry `event`s).
  const clickable =
    entry.type === 'workout' ||
    entry.type === 'activityLog' ||
    !!resolveEntryWebPath(getEntryLink(entry));

  const handleClick = React.useCallback(() => {
    if (disabled) return;

    const { type } = entry;
    analytics.logSelectContent(type, entry.id);

    // WeekDayEntryWorkout (program workout)
    if (entry.type === 'workout') {
      const microCycleDay = (entry as WeekDayEntryWorkout).workout;
      const workoutId = microCycleDay?.coachWorkoutId || microCycleDay?.workout?.id;
      if (workoutId) {
        navigate(`/move/${workoutId}`, {
          state: {
            microCycleDayId: microCycleDay.id,
            workoutLogId: microCycleDay.workoutLog?.id,
          },
        });
      }
      return;
    }

    // WeekDayEntryActivityLog - navigate to activity result
    if (entry.type === 'activityLog') {
      navigate(`/activities/${(entry as WeekDayEntryActivityLog).activityLogId}`);
      return;
    }

    // Other entry types (e.g. scheduled explore-entry `event`s) carry their
    // navigation target in the backend-provided linkProps. Those are authored
    // for the native app (kinastic:// deep links + native screen names), so
    // resolve them to the equivalent web route. Mirrors the native
    // WorkoutDayCardUnifiedItem, which navigates via linkProps for these types.
    const link = getEntryLink(entry);
    const webPath = resolveEntryWebPath(link);
    if (webPath) {
      navigate(webPath);
      return;
    }
    if (link) {
      const parsedLink = LinkUtils.parseLink(link);
      if (LinkUtils.isExternalWebUrl(parsedLink)) {
        window.open(parsedLink, '_blank');
      }
    }
  }, [disabled, navigate, analytics, entry]);

  // Build subtitle from pre-resolved properties
  const displaySubtitle = React.useMemo(() => {
    const parts: string[] = [];
    if (timeText) parts.push(timeText);
    if (durationText) parts.push(durationText);
    if (contextInfo) parts.push(contextInfo);
    if (subtitle) parts.push(subtitle);
    return parts.join(' • ');
  }, [timeText, durationText, contextInfo, subtitle]);

  const handleAddToCalendar = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!date || !title) return;
      downloadScheduleEntryAsIcal(entry, date);
      analytics.logEvent('schedule_entry_calendar_downloaded', { objectId: entry.id, objectType: entry.type });
    },
    [entry, date, title, analytics],
  );

  // Show calendar button for non-done entries that have a date
  const showCalendarButton = !!date && !showAsDone && entry.type !== 'activityLog';

  // Get icon for entry type when no image
  const getTypeIcon = () => {
    switch (entry.type) {
      case 'workout':
        return 'mdi mdi-dumbbell';
      case 'activityLog':
        return 'mdi mdi-run';
      case 'event':
        return 'mdi mdi-calendar';
      case 'mindfulnessUnit':
        return 'mdi mdi-meditation';
      default:
        return 'mdi mdi-lightning-bolt';
    }
  };

  return (
    <Container onClick={handleClick} disabled={disabled} clickable={clickable} done={showAsDone}>
      <ThumbnailContainer>
        {hasImage ? (
          <Thumbnail className="schedule-thumbnail" imageSource={`url(${imageUri})`}>
            {isVideo && (
              <VideoOverlay>
                <PlayIcon className="mdi mdi-play" />
              </VideoOverlay>
            )}
          </Thumbnail>
        ) : (
          <PlaceholderThumbnail>
            <i className={getTypeIcon()} />
          </PlaceholderThumbnail>
        )}
      </ThumbnailContainer>
      <Content>
        {typeName && <TypeTag>{typeName}</TypeTag>}
        {title && <TitleText>{title}</TitleText>}
        {displaySubtitle && <SubtitleText>{displaySubtitle}</SubtitleText>}
      </Content>
      <Actions>
        {showCalendarButton && (
          <CalendarButton onClick={handleAddToCalendar} title="Add to calendar">
            <i className="mdi mdi-calendar-plus" />
          </CalendarButton>
        )}
        <StatusIndicator done={showAsDone}>
          {showAsDone && <i className="mdi mdi-check" />}
        </StatusIndicator>
        {clickable && !showAsDone && <ArrowIndicator className="arrow-indicator">→</ArrowIndicator>}
      </Actions>
    </Container>
  );
});
